import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Paper,
} from '@mui/material';
import BrandLogo from './BrandLogo';
import { getTheme } from '../theme/modernTheme';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const mode = useSelector((state) => state.global.mode);
  const currentTheme = getTheme(mode);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/auth/register', {
        name,
        email,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 201) {
        navigate('/login');
      }
    } catch (err) {
      console.error('Error registering:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Kayıt başarısız. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: currentTheme.colors.gradient.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Paper
        elevation={24}
        sx={{
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 400,
          width: '100%',
          background: currentTheme.colors.surface.primary,
          backdropFilter: currentTheme.effects.backdrop,
          borderRadius: 3,
          border: `1px solid ${currentTheme.colors.border.primary}`,
          boxShadow: currentTheme.shadows.glass
        }}
      >
        <BrandLogo
          size={100}
          sx={{
            mb: 2.5,
            maxWidth: 280,
            mx: "auto",
            objectPosition: "center",
          }}
        />

        <Typography
          component="h1"
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 700,
            color: currentTheme.colors.text.primary,
            textAlign: "center",
          }}
        >
          Kayıt ol
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 3,
            color: currentTheme.colors.text.secondary,
            textAlign: "center",
          }}
        >
          Hesabınızı oluşturun
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          <TextField
            label="Ad Soyad"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
              },
              '& .MuiInputLabel-root': {
                color: currentTheme.colors.text.muted,
                '&.Mui-focused': {
                  color: currentTheme.colors.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: currentTheme.colors.text.primary,
              },
            }}
          />
          <TextField
            label="E-posta"
            type="text"
            name="email"
            id="register-email"
            autoComplete="email"
            inputMode="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: 'false',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
              },
              '& .MuiInputLabel-root': {
                color: currentTheme.colors.text.muted,
                '&.Mui-focused': {
                  color: currentTheme.colors.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: currentTheme.colors.text.primary,
              },
            }}
          />
          <TextField
            label="Şifre"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
              },
              '& .MuiInputLabel-root': {
                color: currentTheme.colors.text.muted,
                '&.Mui-focused': {
                  color: currentTheme.colors.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: currentTheme.colors.text.primary,
              },
            }}
          />
          <Button 
            type="submit" 
            variant="contained" 
            fullWidth
            disabled={loading}
            sx={{ 
              mt: 3, 
              mb: 2,
              height: 48,
              borderRadius: 2,
              background: mode === 'dark' ? '#fafafa' : '#0a0a0a',
              color: mode === 'dark' ? '#0a0a0a' : '#fafafa',
              fontWeight: 600,
              fontSize: '1rem',
              '&:hover': {
                background: mode === 'dark' ? '#e5e5e5' : '#262626',
                transform: 'translateY(-1px)',
                boxShadow: currentTheme.shadows.md
              },
              '&:disabled': {
                background: currentTheme.colors.surface.glass,
                color: currentTheme.colors.text.muted
              }
            }}
          >
            {loading ? 'Kaydediliyor...' : 'Kayıt Ol'}
          </Button>
          
          <Button
            variant="text"
            fullWidth
            onClick={() => navigate('/login')}
            sx={{
              color: currentTheme.colors.text.secondary,
              textTransform: 'none',
              '&:hover': {
                color: currentTheme.colors.primary,
                background: 'transparent'
              }
            }}
          >
            Zaten hesabınız var mı? Giriş Yapın
          </Button>
          
          {error && (
            <Alert 
              severity="error" 
              variant="filled"
              sx={{ 
                mt: 2,
                borderRadius: 2,
                bgcolor: '#ef4444',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' }
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Register;
